
import {
  Box,
  CircularProgress,
  Divider,
  IconButton
} from "@mui/material";
import { getIn, useFormikContext } from "formik";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { ConversationService } from "../../../../Services/Apis";
import { useBotConfigStore } from "../../../../Store/Slices/ChatbotSlices/BotConfigSlice";
import { AppMaterialIcons } from "../../Icons";
import { useAppSelector } from "../../../../Store";
import { botInfoStore } from "../../../../Store/Slices/socket/BotInfoSlice";
import { getUploadedMediaType } from "./utils";

enum ImageState {
  INITIAL = "initial",
  SEARCH = "search",
  GALLERY = "gallery",
  UPLOADED = "uploaded",
}

const getCorrectMediaSize = (type: string) => {
  var size: number = 5242880; // Default 5MB for images

  if (type === "Video") {
    size = 16777216; // 16.00 MB
  } else if (type === "Images") {
    size = 5242880; // 5.00 MB
  } else if (type === "Documents") {
    size = 104857600; // 100.00 MB
  } else if (type === "Sticker") {
    size = 5242880; // 5.00 MB (same as images)
  } else if (type === "Audio") {
    size = 5242880; // 5.00 MB
  }

  return size;
};

interface Props {
  name?: string;
  icon?: string;
  allowedMimeTypes?: string[];
  allowedExtensions?: string[];
}

const EditorMediaUpload: React.FC<Props> = ({
  name = "media",
  icon = "AttachFile",
  allowedMimeTypes = [],
  allowedExtensions = [],
}) => {
  const {
    values,
    setFieldError,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();
  const { getaHost } = useAppSelector(botInfoStore);

  const [file, setFile] = React.useState<any>(getIn(values, "media"));
  const fileInputRef = React.useRef<any>(undefined);
  const handleIconClick = () => {
    fileInputRef?.current?.click();
  };

  React.useEffect(() => {
    if (!getIn(values, "media")) {
      imageResetHandler();
    }
  }, [getIn(values, "media")]); // eslint-disable-line react-hooks/exhaustive-deps

  const [mainState, setMainState] = useState<ImageState>(ImageState.INITIAL); // initial, search, gallery, uploaded
  const [uploadMediaType, setUploadMediaType] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);

  const handleUploadClick = (event: any) => {
    setFieldTouched(name, true);

    var file = event.target.files[0];
    const reader = new FileReader();
    var url = reader?.readAsDataURL(file);

    reader.onloadend = (e: any) => {
      let mimeType: any = e?.target?.result
        ?.split(",")[0]
        .split(":")[1]
        .split(";")[0];

      // Check if the uploaded file type is allowed
      const isValidMimeType = allowedMimeTypes.length === 0 || allowedMimeTypes.includes(mimeType);
      
      if (!isValidMimeType) {
        console.log("Invalid file format. Allowed types:", allowedMimeTypes);
        setTimeout(() => {
          const allowedExtensionsText = allowedExtensions.join(', ');
          setFieldError(name, `Invalid file format. Only ${allowedExtensionsText} files are allowed.`);
        }, 10);
        return;
      }

      let uploadExtensionMediaType: string = "";
      const mediaType = getUploadedMediaType(mimeType);
      
      // Map the media type to match the format expected by getCorrectMediaSize
      if (mediaType === "image") {
        uploadExtensionMediaType = "Images";
      } else if (mediaType === "video") {
        uploadExtensionMediaType = "Video";
      } else if (mediaType === "document") {
        uploadExtensionMediaType = "Documents";
      } else if (mediaType === "audio") {
        uploadExtensionMediaType = "Audio";
      } else {
        uploadExtensionMediaType = "Documents"; // Default fallback
      }

      const allowedFileSize = getCorrectMediaSize(uploadExtensionMediaType);
      if (allowedFileSize && file?.size > allowedFileSize) {
        setFieldError(name, `File must be under ${Math.round(allowedFileSize / 1048576)} MB`);
        return;
      }

      setFieldError(name, "");
      onImageChange(file);
    };
  };

  //   Make upload api call
  const { botConfig } = useSelector(useBotConfigStore);
  const HEADERS = {
    appid: JSON.stringify({
      botid: botConfig?.chatbot?.bot_id || "",
    }),
    "GETA-HOST": getaHost,
  };
  const onImageChange = (file: any) => {
    setFieldTouched(name, true);
    if (file) {
      setLoading(true);
      const formData = new FormData();
      formData.append("file", file);
      ConversationService.mediaUpload(formData, HEADERS)
        .then((response: any) => {
          setFieldValue(name, response.data);
          setMainState(ImageState.UPLOADED);
          setLoading(false);
        })
        .catch((error: any) => {
          setFieldError(name, `Media upload failed. Please try again.`);
          setLoading(false);
        })
        .finally(() => {});
    }
  };

  const imageResetHandler = () => {
    setMainState(ImageState.INITIAL);
    setFile(null);
    setFieldValue(name, null);
    setUploadMediaType("");
  };

  return (
    <>
      <input
        accept={allowedExtensions.length > 0 ? allowedExtensions.map(ext => `.${ext}`).join(',') : "*"}
        id={name}
        name={name}
        type="file"
        onChange={handleUploadClick}
        value=""
        style={{ display: "none" }} // to hide the input
        ref={fileInputRef}
      />
      <Box
        sx={{
          position: "absolute",
          right: "50px",
        }}
      >
        {loading ? (
          <Box
            sx={{
              width: "42px",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={16} />
          </Box>
        ) : getIn(values, "media")?.name ||
          getIn(values, "media")?.file_name ? null : (
          <Box display="flex">
            <Divider orientation="vertical" variant="middle" flexItem />
            <StyledLabel htmlFor={name}>
              <IconButton title={"Upload"} onClick={handleIconClick}>
                <AppMaterialIcons
                  iconName={icon}
                  style={{ fontSize: "1.2rem" }}
                />
              </IconButton>
            </StyledLabel>
          </Box>
        )}
      </Box>
    </>
  );
};

export default EditorMediaUpload;

const StyledLabel = styled.label`
  && {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-right: 6px;
    margin-left: 4px;

    width: 32px;
    height: 32px;
    border-radius: 50%;
    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }
`;

